import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import Utils from "../Utils";
import i18n from "../i18n";
import services from "../services";

const strict = true;

const getDefaultState = () => {
  return {
    memberAppointmentList: {
      filtered: [],
      full: []
    },
    toSearch: "",
    multiSelectConfig: {
      items: [
        "PENDING",
        "ACCEPTED",
        "ACCEPTED_POST",
        "TOBERESCHEDULED",
        "MEMBERCHANGE",
        "ADMINISTRATIVE_CHECK_REQUIRED"
      ],
      activeItems: []
    },
    dateConfig: {
      range: {
        dateFrom: null,
        dateTo: null
      },
      dateToUseConfig: {
        active: "REQUEST",
        options: ["SCHEDULE", "REQUEST"]
      }
    },
    filterCriteriaResults: "",
    validStates: [
      "PENDING",
      "ACCEPTED",
      "TOBERESCHEDULED",
      "MEMBERCHANGE",
      "ADMINISTRATIVE_CHECK_REQUIRED"
    ]
  };
};

const state = getDefaultState();

const getters = {
  items: state => state.memberAppointmentList.filtered,
  itemsFullList: state => state.memberAppointmentList.full,
  toSearch: state => state.toSearch,
  totalItems: state => state.memberAppointmentList.filtered.length,
  multiSelectFullList: state => state.multiSelectConfig.items,
  multiSelectActiveList: state => state.multiSelectConfig.activeItems,
  dateRange: state => state.dateConfig.range,
  dateToUseOptions: state => state.dateConfig.dateToUseConfig.options,
  dateToUse: state => state.dateConfig.dateToUseConfig.active,
  filterCriteriaResults: state => state.filterCriteriaResults,
  categoryByType: state => type => state.categories[type]
};

const mutations = {
  RESET(state) {
    // acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_TO_SEARCH(state, toSearch) {
    state.toSearch = toSearch;
  },
  SET_MULTI_SELECT_ACTIVE_LIST(state, multiSelectList) {
    state.multiSelectConfig.activeItems = multiSelectList;
  },
  updateDateRange(state, dateRange) {
    state.dateConfig.range = dateRange;
  },
  updateDateToUse(state, dateToUse) {
    state.dateConfig.dateToUseConfig.active = dateToUse;
  },
  SET_FILTER_CRITERIA_RESULTS(state) {
    // text criteria
    let criteria = [];
    if (state.toSearch) {
      criteria.push("'" + state.toSearch + "'");
    }

    // select criteria
    let activeMultiSelectItems = state.multiSelectConfig.activeItems;
    if (!isEmpty(activeMultiSelectItems)) {
      let translatedActiveItems = [];
      activeMultiSelectItems.forEach(item => {
        translatedActiveItems.push(
          i18n.t(`baseFilters.members.${item}Checkbox`).toUpperCase()
        );
      });
      criteria.push("'" + translatedActiveItems.join(", ") + "'");
    }

    // date criteria
    let dateFrom = state.dateConfig.range.dateFrom;
    let dateTo = state.dateConfig.range.dateTo;
    if (dateFrom || dateTo) {
      let formatDateFrom = dateFrom ? dayjs(dateFrom).format("DD/MM/YYYY") : "";
      let formatDateTo = dateTo ? dayjs(dateTo).format("DD/MM/YYYY") : "";
      criteria.push("'" + formatDateFrom + " - " + formatDateTo + "'");
    }
    state.filterCriteriaResults = criteria.join(" & ");
  },
  setMemberAppointmentFullList(state, memberAppointmentList) {
    for (let appointment of memberAppointmentList) {
      if (appointment.state == "ACCEPTED" && !appointment.acceptedToWork) {
        appointment.state = "ACCEPTED_POST";
      }
    }
    state.memberAppointmentList.full = memberAppointmentList;
  },
  setMemberAppointmentFilteredList(state, memberAppointmentList) {
    state.memberAppointmentList.filtered = memberAppointmentList;
  }
};

const actions = {
  /**
    @param commit
    @param id : memberId
    @param officeId
  */
  fetchMemberAppointmentList({ commit, dispatch }, { id, officeId }) {
    return services.membersService
      .fetchMemberAppointments(id, officeId, {
        state: state.validStates.join(",")
      })
      .then(memberAppointmentList => {
        commit("setMemberAppointmentFullList", memberAppointmentList);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "members";
        }
        commit("setMemberAppointmentFullList", []);
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {
        dispatch("filterMemberAppointmentList"); //TOCHANGE add it on success
      });
  },
  filterMemberAppointmentList({ commit, state }) {
    let appointmentFullList = state.memberAppointmentList.full;
    return filterAppointmentListPromise(
      appointmentFullList,
      state.toSearch,
      state.multiSelectConfig.activeItems,
      state.dateConfig
    )
      .then(filteredList => {
        commit("setMemberAppointmentFilteredList", filteredList);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        commit("SET_FILTER_CRITERIA_RESULTS");
        let memberAppointmentListElement = document.querySelector(
          ".appointment-list-container"
        );
        if (memberAppointmentListElement) {
          memberAppointmentListElement.scrollTo({
            top: 0,
            behavior: "auto"
          });
        }
      });
  }
};

function filterAppointmentListPromise(
  appointmentList,
  toSearch,
  states,
  dateConfig
) {
  return new Promise((resolve, reject) => {
    try {
      let searchList =
        toSearch?.toLowerCase().split(/\s+/).filter(Boolean) || [];

      let filteredAppointmentList = appointmentList.filter(appointment => {
        // text filter
        let serviceName = appointment.hService?.longDescr;
        let serviceShortName = appointment.hService?.shortDescr;
        let iupCode = appointment.iupCode?.toLowerCase();

        let serviceNameWords = serviceName?.toLowerCase().split(/\s+/) || [];
        let serviceShortNameWords =
          serviceShortName?.toLowerCase().split(/\s+/) || [];
        let allWords = [
          ...new Set(
            Utils.normalizeServiceWords([
              ...serviceNameWords,
              ...serviceShortNameWords
            ])
          )
        ];
        if (iupCode) {
          allWords.push(iupCode);
        }

        let textMatches =
          searchList.length == 0 ||
          searchList.every(str => allWords.some(w => w.startsWith(str)));

        // state filter
        let stateMatches =
          states.length == 0 || states.includes(appointment.state);

        // date filter
        let { dateFrom, dateTo } = dateConfig.range;
        let dateToUse = dateConfig.dateToUseConfig.active;
        let date =
          dateToUse == "SCHEDULE"
            ? appointment.appointmentScheduleDate
            : appointment.appointmentRequestDate;

        let dateMatches = true;
        if (dateFrom || dateTo) {
          if (!date) {
            dateMatches = false;
          } else {
            let day = dayjs(date);
            let from = dateFrom && dayjs(dateFrom);
            let to = dateTo && dayjs(dateTo);

            if (from && to) {
              dateMatches = day.isBetween(from, to, "day", "[]");
            } else if (from) {
              dateMatches = day.isSameOrAfter(from, "day");
            } else if (to) {
              dateMatches = day.isSameOrBefore(to, "day");
            }
          }
        }

        return textMatches && stateMatches && dateMatches;
      });

      resolve(filteredAppointmentList);
    } catch (error) {
      reject(error);
    }
  });
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
